import React from "react";
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff } from "lucide-react";
import Call, { CallDirection, CallStatus } from "@/model/interface/Call";
import { Badge } from "@/components/ui/badge";
import { RecordingSource } from "@/enum/RecordingSource";
import { isMachineAnsweredBy } from "@/enum/AnsweredBy";

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
}

export function getContactDisplayName(contact?: { firstName?: string; lastName?: string } | null): string | null {
  if (!contact) {
    return null;
  }
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
  return name || null;
}

export function getCallIcon(direction: CallDirection, status: CallStatus) {
  if (isMissedCall(direction, status)) {
    return <PhoneMissed className="h-4 w-4 text-destructive" />;
  }
  if (isUnansweredCall(status)) {
    return <PhoneOff className="h-4 w-4 text-muted-foreground" />;
  }
  if (direction === CallDirection.INBOUND) {
    return <PhoneIncoming className="h-4 w-4 text-success" />;
  }
  return <PhoneOutgoing className="h-4 w-4 text-info" />;
}

export function isUnansweredCall(status: CallStatus): boolean {
  return status === CallStatus.NO_ANSWER || status === CallStatus.BUSY || status === CallStatus.CANCELED || status === CallStatus.MISSED;
}

export function isMissedCall(direction: CallDirection, status: CallStatus): boolean {
  return direction === CallDirection.INBOUND && isUnansweredCall(status);
}

export function getUnansweredBadge(direction: CallDirection, status: CallStatus) {
  if (!isUnansweredCall(status)) {
    return null;
  }
  if (isMissedCall(direction, status)) {
    return <Badge variant="destructive" className="text-xs">Missed</Badge>;
  }
  return <Badge variant="secondary" className="text-xs">No answer</Badge>;
}

export function isVoicemail(call: Call): boolean {
  return call.recording?.source === RecordingSource.VOICEMAIL || isMachineAnsweredBy(call.answeredBy);
}

export function groupCallsByDate(calls: Call[]): Map<string, Call[]> {
  const groups = new Map<string, Call[]>();

  calls.forEach((call) => {
    const dateKey = formatDate(new Date(call.createdAt!));
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(call);
  });

  return groups;
}
